import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Loader2, Plus, RefreshCw, Upload, Pencil, Trash2, X, ChevronRight, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const userDisplayName = (user) => {
  if (!user) return '';
  return String(user.full_name || user.username || '').trim();
};

const sanitizeCurrencyText = (value) => {
  if (value == null || value === '') return value == null ? null : '';
  return String(value)
    .replace(/\u0080/g, '€')
    .replace(/â‚¬/g, '€')
    .replace(/Â£/g, '£');
};

const emptyForm = (defaultAddedBy = '') => ({
  site_url: '',
  ahrefs_dr: '',
  organic_traffic: '',
  geo_traffic_raw: '',
  contact_raw: '',
  price_raw: '',
  price_amount: '',
  price_currency: 'GBP',
  comments: '',
  accepts_casino: 'unknown',
  added_by: defaultAddedBy,
  market_uk: true,
  market_us: false,
  market_ie: false,
  category: '',
  is_active: true
});

const formatTraffic = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString('en-GB');
};

const formatPrice = (site) => {
  const raw = sanitizeCurrencyText(site.price_raw);
  if (raw) return raw;
  if (site.price_amount == null) return '—';
  const symbol = site.price_currency === 'GBP' ? '£' : site.price_currency === 'USD' ? '$' : site.price_currency === 'EUR' ? '€' : '';
  return `${symbol}${Number(site.price_amount)}${symbol ? '' : ` ${site.price_currency || ''}`.trimEnd()}`;
};

const marketsLabel = (site) => {
  const parts = [];
  if (site.market_uk) parts.push('UK');
  if (site.market_us) parts.push('US');
  if (site.market_ie) parts.push('IE');
  return parts.length ? parts.join(' · ') : '—';
};

const PAGE_SIZE = 100;

const LinkDirectory = () => {
  const { user } = useAuth();
  const currentUserLabel = userDisplayName(user);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ source_sheets: [], currencies: [] });
  const [filters, setFilters] = useState({
    q: '',
    min_dr: '',
    max_price: '',
    currency: '',
    category: '',
    market_uk: false,
    market_us: false,
    market_ie: false,
    accepts_casino: '',
    added_by: '',
    sort: 'domain'
  });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [sourceSheet, setSourceSheet] = useState('uk_guest');
  const [importing, setImporting] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState(null);

  const updateFilters = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
    setExpandedRowId(null);
  }, []);

  const loadMeta = useCallback(async () => {
    const [metaRes, catRes, usersRes] = await Promise.all([
      axios.get('/api/link-directory/meta'),
      axios.get('/api/link-directory/categories'),
      axios.get('/api/users')
    ]);
    setMeta(metaRes.data || { source_sheets: [], currencies: [] });
    setCategories(Array.isArray(catRes.data) ? catRes.data : []);
    setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    if (metaRes.data?.source_sheets?.[0]?.id) {
      setSourceSheet(metaRes.data.source_sheets[0].id);
    }
  }, []);

  const loadSites = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        sort: filters.sort
      };
      if (filters.q) params.q = filters.q;
      if (filters.min_dr !== '') params.min_dr = filters.min_dr;
      if (filters.max_price !== '') params.max_price = filters.max_price;
      if (filters.currency) params.currency = filters.currency;
      if (filters.category) params.category = filters.category;
      if (filters.accepts_casino) params.accepts_casino = filters.accepts_casino;
      if (filters.added_by) params.added_by = filters.added_by;
      if (filters.market_uk) params.market_uk = '1';
      if (filters.market_us) params.market_us = '1';
      if (filters.market_ie) params.market_ie = '1';
      const response = await axios.get('/api/link-directory/sites', { params });
      setSites(response.data.sites || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load link directory');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadMeta().catch(() => toast.error('Failed to load directory metadata'));
  }, [loadMeta]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm(currentUserLabel));
    setShowForm(true);
  };

  const openEdit = (site) => {
    setEditingId(site.id);
    setForm({
      site_url: site.site_url || site.domain || '',
      ahrefs_dr: site.ahrefs_dr ?? '',
      organic_traffic: site.organic_traffic ?? '',
      geo_traffic_raw: site.geo_traffic_raw || '',
      contact_raw: site.contact_raw || site.contact_email || '',
      price_raw: site.price_raw || '',
      price_amount: site.price_amount ?? '',
      price_currency: site.price_currency || 'GBP',
      comments: site.comments || '',
      accepts_casino: site.accepts_casino || 'unknown',
      added_by: site.added_by || '',
      market_uk: Boolean(site.market_uk),
      market_us: Boolean(site.market_us),
      market_ie: Boolean(site.market_ie),
      category: (site.categories || []).map((c) => c.name).join(', '),
      is_active: Boolean(site.is_active)
    });
    setShowForm(true);
  };

  const saveSite = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        ahrefs_dr: form.ahrefs_dr === '' ? null : Number(form.ahrefs_dr),
        organic_traffic: form.organic_traffic === '' ? null : Number(form.organic_traffic),
        price_amount: form.price_amount === '' ? null : Number(form.price_amount),
        categories: form.category
          ? form.category.split(/[/|,]/).map((part) => part.trim()).filter(Boolean)
          : []
      };
      if (editingId) {
        await axios.put(`/api/link-directory/sites/${editingId}`, payload);
        toast.success('Site updated');
      } else {
        await axios.post('/api/link-directory/sites', payload);
        toast.success('Site added');
      }
      setShowForm(false);
      await Promise.all([loadSites(), loadMeta()]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save site');
    } finally {
      setSaving(false);
    }
  };

  const deleteSite = async (site) => {
    if (!window.confirm(`Remove ${site.domain} from the directory?`)) return;
    try {
      await axios.delete(`/api/link-directory/sites/${site.id}`);
      toast.success('Site removed');
      loadSites();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete site');
    }
  };

  const runImport = async (event) => {
    event.preventDefault();
    if (!importFile) {
      toast.error('Choose a CSV file');
      return;
    }
    setImporting(true);
    try {
      const body = new FormData();
      body.append('file', importFile);
      body.append('source_sheet', sourceSheet);
      const response = await axios.post('/api/link-directory/import', body);
      toast.success(
        `Import complete: ${response.data.inserted} added, ${response.data.updated} updated, ${response.data.skipped} skipped`
      );
      setShowImport(false);
      setImportFile(null);
      await Promise.all([loadSites(), loadMeta()]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const addedByOptions = useMemo(() => {
    const labels = users
      .map((u) => userDisplayName(u))
      .filter(Boolean);
    const unique = [...new Set(labels)].sort((a, b) => a.localeCompare(b));
    if (form.added_by && !unique.includes(form.added_by)) {
      unique.unshift(form.added_by);
    }
    return unique;
  }, [users, form.added_by]);

  const filterAddedByOptions = useMemo(() => {
    const labels = users.map((u) => userDisplayName(u)).filter(Boolean);
    sites.forEach((site) => {
      if (site.added_by) labels.push(String(site.added_by).trim());
    });
    return [...new Set(labels.filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [users, sites]);

  const filterSummary = useMemo(() => {
    const bits = [];
    if (filters.q) bits.push(`search “${filters.q}”`);
    if (filters.min_dr !== '') bits.push(`DR ≥ ${filters.min_dr}`);
    if (filters.max_price !== '') bits.push(`price ≤ ${filters.max_price}`);
    if (filters.category) bits.push(filters.category);
    return bits.length ? bits.join(' · ') : 'all sites';
  }, [filters]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Link Directory</h2>
          <p className="text-gray-600">
            Company-wide inventory of guest-post sites. Import from the existing Google Sheets CSVs, then filter by DR, market, category, and price.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn btn-secondary inline-flex items-center gap-2" onClick={loadSites}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button type="button" className="btn btn-secondary inline-flex items-center gap-2" onClick={() => setShowImport(true)}>
            <Upload className="h-4 w-4" />
            Import CSV
          </button>
          <button type="button" className="btn btn-primary inline-flex items-center gap-2" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            Add Site
          </button>
        </div>
      </div>

      <div className="card space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="label" htmlFor="ld-q">Search</label>
            <input
              id="ld-q"
              className="input"
              placeholder="Domain, contact, comments…"
              value={filters.q}
              onChange={(e) => updateFilters({ q: e.target.value })}
            />
          </div>
          <div>
            <label className="label" htmlFor="ld-min-dr">Min DR</label>
            <input
              id="ld-min-dr"
              type="number"
              className="input"
              min="0"
              max="100"
              value={filters.min_dr}
              onChange={(e) => updateFilters({ min_dr: e.target.value })}
            />
          </div>
          <div>
            <label className="label" htmlFor="ld-max-price">Max price</label>
            <input
              id="ld-max-price"
              type="number"
              className="input"
              min="0"
              value={filters.max_price}
              onChange={(e) => updateFilters({ max_price: e.target.value })}
            />
          </div>
          <div>
            <label className="label" htmlFor="ld-category">Category</label>
            <select
              id="ld-category"
              className="input"
              value={filters.category}
              onChange={(e) => updateFilters({ category: e.target.value })}
            >
              <option value="">All categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.slug}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="ld-currency">Currency</label>
            <select
              id="ld-currency"
              className="input"
              value={filters.currency}
              onChange={(e) => updateFilters({ currency: e.target.value })}
            >
              <option value="">Any</option>
              {(meta.currencies || ['GBP', 'USD', 'EUR', 'OTHER']).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="ld-casino">Accepts casino</label>
            <select
              id="ld-casino"
              className="input"
              value={filters.accepts_casino}
              onChange={(e) => updateFilters({ accepts_casino: e.target.value })}
            >
              <option value="">Any</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor="ld-added-by">Added by</label>
            <select
              id="ld-added-by"
              className="input"
              value={filters.added_by}
              onChange={(e) => updateFilters({ added_by: e.target.value })}
            >
              <option value="">Anyone</option>
              {filterAddedByOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="ld-sort">Sort</label>
            <select
              id="ld-sort"
              className="input"
              value={filters.sort}
              onChange={(e) => updateFilters({ sort: e.target.value })}
            >
              <option value="domain">Domain A–Z</option>
              <option value="dr_desc">DR high → low</option>
              <option value="dr_asc">DR low → high</option>
              <option value="price_asc">Price low → high</option>
              <option value="price_desc">Price high → low</option>
              <option value="traffic_desc">Traffic high → low</option>
              <option value="updated">Recently updated</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={filters.market_uk}
              onChange={(e) => updateFilters({ market_uk: e.target.checked })}
            />
            UK
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={filters.market_us}
              onChange={(e) => updateFilters({ market_us: e.target.checked })}
            />
            US
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={filters.market_ie}
              onChange={(e) => updateFilters({ market_ie: e.target.checked })}
            />
            IE
          </label>
          <p className="text-sm text-gray-500 ml-auto">
            Showing {rangeStart}–{rangeEnd} of {total} ({filterSummary})
          </p>
        </div>
      </div>

      <div className="card overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
          </div>
        ) : sites.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-gray-700 font-medium">No sites in the directory yet</p>
            <p className="text-sm text-gray-500 mt-2 max-w-lg mx-auto">
              Import CSV exports from the UK / UK+USA Guest Posting and Sports Google Sheets, or add a site manually.
            </p>
            <button type="button" className="btn btn-primary mt-4" onClick={() => setShowImport(true)}>
              Import CSV
            </button>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[22%]">Domain</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[8%]">DR</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[10%]">Traffic</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[16%]">Geo</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[12%]">Price</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[14%]">Category</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[10%]">Markets</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase w-[8%]">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sites.map((site) => {
                const isExpanded = String(expandedRowId) === String(site.id);
                const comments = sanitizeCurrencyText(site.comments);
                const contact = site.contact_email || site.contact_raw || '—';
                return (
                  <React.Fragment key={site.id}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedRowId(isExpanded ? null : site.id)}
                    >
                      <td className="px-3 py-3 text-sm font-medium text-gray-900">
                        <div className="flex items-center gap-2 min-w-0">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />
                          )}
                          <a
                            href={site.site_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary-700 hover:underline truncate"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {site.domain}
                          </a>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-800">{site.ahrefs_dr ?? '—'}</td>
                      <td className="px-3 py-3 text-sm text-gray-800">{formatTraffic(site.organic_traffic)}</td>
                      <td className="px-3 py-3 text-sm text-gray-600 truncate" title={site.geo_traffic_raw || ''}>
                        {site.geo_traffic_raw || '—'}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-800 whitespace-nowrap">{formatPrice(site)}</td>
                      <td className="px-3 py-3 text-sm text-gray-700 truncate">
                        {(site.categories || []).map((c) => c.name).join(', ') || '—'}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">{marketsLabel(site)}</td>
                      <td className="px-3 py-3 text-right text-sm whitespace-nowrap">
                        <button
                          type="button"
                          className="btn btn-secondary py-1 px-2 mr-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(site);
                          }}
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger py-1 px-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSite(site);
                          }}
                          title="Remove"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan={8} className="px-4 pb-4 pt-0">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="border border-gray-200 rounded-lg p-3 bg-white">
                              <p className="text-xs uppercase text-gray-500 mb-2">Contact</p>
                              <p className="text-sm text-gray-700 break-all">{contact}</p>
                            </div>
                            <div className="border border-gray-200 rounded-lg p-3 bg-white">
                              <p className="text-xs uppercase text-gray-500 mb-2">Added by</p>
                              <p className="text-sm text-gray-700">{site.added_by || '—'}</p>
                            </div>
                            <div className="md:col-span-2 border border-gray-200 rounded-lg p-3 bg-white">
                              <p className="text-xs uppercase text-gray-500 mb-2">Comments</p>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">{comments || '—'}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!loading && total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={page <= 1}
              onClick={() => {
                setExpandedRowId(null);
                setPage((prev) => Math.max(1, prev - 1));
              }}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={page >= totalPages}
              onClick={() => {
                setExpandedRowId(null);
                setPage((prev) => Math.min(totalPages, prev + 1));
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">{editingId ? 'Edit site' : 'Add site'}</h3>
              <button type="button" onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-800">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={saveSite} className="flex flex-col min-h-0 flex-1">
              <div className="p-4 space-y-2.5 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  <div className="md:col-span-2">
                    <label className="label" htmlFor="ld-site-url">Site URL</label>
                    <input id="ld-site-url" className="input" required value={form.site_url} onChange={(e) => setForm((p) => ({ ...p, site_url: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label" htmlFor="ld-dr">DR</label>
                    <input id="ld-dr" type="number" className="input" value={form.ahrefs_dr} onChange={(e) => setForm((p) => ({ ...p, ahrefs_dr: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label" htmlFor="ld-traffic">Organic traffic</label>
                    <input id="ld-traffic" type="number" className="input" value={form.organic_traffic} onChange={(e) => setForm((p) => ({ ...p, organic_traffic: e.target.value }))} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label" htmlFor="ld-geo">Geo traffic</label>
                    <input id="ld-geo" className="input" placeholder="e.g. 2k UK 16k USA" value={form.geo_traffic_raw} onChange={(e) => setForm((p) => ({ ...p, geo_traffic_raw: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label" htmlFor="ld-price-raw">Price (raw)</label>
                    <input id="ld-price-raw" className="input" placeholder="£80" value={form.price_raw} onChange={(e) => setForm((p) => ({ ...p, price_raw: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label" htmlFor="ld-price-amount">Price amount</label>
                    <input id="ld-price-amount" type="number" className="input" value={form.price_amount} onChange={(e) => setForm((p) => ({ ...p, price_amount: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label" htmlFor="ld-price-currency">Currency</label>
                    <select id="ld-price-currency" className="input" value={form.price_currency} onChange={(e) => setForm((p) => ({ ...p, price_currency: e.target.value }))}>
                      <option value="GBP">GBP</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="OTHER">OTHER</option>
                    </select>
                  </div>
                  <div>
                    <label className="label" htmlFor="ld-casino-form">Accepts casino</label>
                    <select id="ld-casino-form" className="input" value={form.accepts_casino} onChange={(e) => setForm((p) => ({ ...p, accepts_casino: e.target.value }))}>
                      <option value="unknown">Unknown</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="label" htmlFor="ld-contact">Contact</label>
                    <input id="ld-contact" className="input" value={form.contact_raw} onChange={(e) => setForm((p) => ({ ...p, contact_raw: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label" htmlFor="ld-added">Added by</label>
                    <select
                      id="ld-added"
                      className="input"
                      value={form.added_by}
                      onChange={(e) => setForm((p) => ({ ...p, added_by: e.target.value }))}
                    >
                      <option value="">Select user</option>
                      {addedByOptions.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label" htmlFor="ld-cats">Categories</label>
                    <input id="ld-cats" className="input" placeholder="News, Lifestyle" value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label" htmlFor="ld-comments">Comments</label>
                    <textarea id="ld-comments" className="input" rows={2} value={form.comments} onChange={(e) => setForm((p) => ({ ...p, comments: e.target.value }))} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 pt-0.5">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.market_uk} onChange={(e) => setForm((p) => ({ ...p, market_uk: e.target.checked }))} />
                    UK
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.market_us} onChange={(e) => setForm((p) => ({ ...p, market_us: e.target.checked }))} />
                    US
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.market_ie} onChange={(e) => setForm((p) => ({ ...p, market_ie: e.target.checked }))} />
                    IE
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 px-4 py-3 border-t shrink-0 bg-white">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary inline-flex items-center gap-2" disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingId ? 'Save changes' : 'Add site'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImport && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Import CSV</h3>
              <button type="button" onClick={() => setShowImport(false)} className="text-gray-500 hover:text-gray-800">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={runImport} className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                Use the template headers (or your Google Sheets export). Choose the matching source sheet so markets are set correctly.
              </p>
              <p className="text-sm text-gray-600">
                <a
                  href="/templates/link-directory-import-template.csv"
                  className="text-primary-600 hover:text-primary-700 underline"
                  download
                >
                  Download import template
                </a>
              </p>
              <div>
                <label className="label" htmlFor="ld-source-sheet">Source sheet</label>
                <select
                  id="ld-source-sheet"
                  className="input"
                  value={sourceSheet}
                  onChange={(e) => setSourceSheet(e.target.value)}
                >
                  {(meta.source_sheets || []).map((sheet) => (
                    <option key={sheet.id} value={sheet.id}>{sheet.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Sets market flags (UK / US / IE) for every row in this file. Duplicate domains update the existing site.
                </p>
              </div>
              <div>
                <label className="label" htmlFor="ld-csv-file">CSV file</label>
                <input
                  id="ld-csv-file"
                  type="file"
                  accept=".csv,text/csv"
                  className="input"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" className="btn btn-secondary" onClick={() => setShowImport(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary inline-flex items-center gap-2" disabled={importing}>
                  {importing && <Loader2 className="h-4 w-4 animate-spin" />}
                  Import
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkDirectory;
